import axios from "@/inside/axios";
import AccountPermission from "@/inside/models/Account/Permission";
import TextbasePermission from "@/inside/models/Textbase/Permission";

export default {
    actions: {
        /**
         * Initializes module state.
         *
         * @param {function} commit
         * @return {Promise}
         */
        fetch({commit}) {
            return new Promise((resolve, reject) => {
                axios.get("/my/permissions", {
                    validateStatus: (status => status === 200)
                }).then((response) => {
                    commit("setAccountPermission", Object.freeze(AccountPermission.fromAPI(response.data.account)));

                    commit("setAllTextbasePermission",
                        response.data.textbases.map((textbase_permission) => {
                            return Object.freeze(TextbasePermission.fromAPI(textbase_permission));
                        }).filter((textbase_permission) => {
                            return textbase_permission.hasAny
                        })
                    );

                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            });
        }
    },
    getters: {
        /**
         * Returns account-level permissions stored in module state.
         *
         * @param {object} state
         * @return {Readonly<AccountPermission>}
         */
        account(state) {
            return state.account;
        },
        /**
         * Returns all text-base level permissions stored in module state.
         *
         * @param {object} state
         * @return {Array<Readonly<TextbasePermission>>}
         */
        allTextbasePermissions(state) {
            return state.textbases;
        },
        /**
         * Returns all text-bases corresponding to text-base level permissions stored in module state.
         *
         * @return {Array<Readonly<Textbase>>}
         */
        allTextbases(state) {
            return state.textbases.map((permission) => {
                return permission.textbase;
            });
        },
        /**
         * Returns all text-bases corresponding:
         * - to text-base permissions stored in module state;
         * - to currently applied filters.
         *
         * @param {object} state
         * @param {object} getters
         * @param {object} rootState
         * @param {object} rootGetters
         * @return {Array<Readonly<Textbase>>}
         */
        filteredTextbases(state, getters, rootState, rootGetters) {
            const filter = rootGetters["filters/one"]("global.sites.id");

            if (!filter) {
                return state.textbases.map((permission) => {
                    return permission.textbase;
                });
            }

            const out = [];

            state.textbases.forEach((permission) => {
                if (!filter.value.includes(permission.textbase.id)) {
                    out.push(permission.textbase);
                }
            });

            return out;
        },
        /**
         * Returns all text-bases corresponding to text-base permissions stored in module state, filtered according to
         * new model filters.
         * @param {object} state
         * @param {object} getters
         * @param {object} rootState
         * @param {object} rootGetters
         * @return {Array<Readonly<Textbase>>}
         */
        filteredTextbasesNG(state, getters, rootState, rootGetters) {
            const filter = rootGetters["ngFilters/store"].textbases;

            if (filter.isEmpty) {
                return state.textbases.map((permission) => {
                    return permission.textbase;
                });
            }

            const out = [];

            state.textbases.forEach((permission) => {
                if (!filter.isExcluded(permission.textbase.id)) {
                    out.push(permission.textbase);
                }
            });

            return out;
        },
        /**
         * Returns a boolean indicating whether current user is admin of any text-base.
         * @return {Boolean}
         */
        isAdminForAnyTextbase(state) {
            const typicalTextbaseAdminPermissionMap = new TextbasePermission();
            Object.assign(typicalTextbaseAdminPermissionMap.map, {
                actions: TextbasePermission.accessReadWrite,
                assessments: TextbasePermission.accessReadWrite,
                assignments: TextbasePermission.accessReadWrite,
                icpe: TextbasePermission.accessReadWrite,
                permissions: TextbasePermission.accessReadWrite,
                quiz: TextbasePermission.accessReadWrite
            });

            const typicalTextbaseAdminPermissionString = typicalTextbaseAdminPermissionMap.toString();

            return state.textbases.find((textbase_level_permissions) => {
                return textbase_level_permissions.toString() === typicalTextbaseAdminPermissionString;
            }) !== undefined;
        },
        /**
         * Returns a boolean indicating whether current user has sufficient permissions to access "admin" section.
         * @param {Object} state
         * @param {Object} getters
         * @return {Boolean}
         */
        hasAdminAccess(state, getters) {
            const hasAccountLevelPermissions = (Object.values(state.account.map).find((permission_value) => {
                return permission_value !== AccountPermission.accessNone;
            }) !== undefined);

            return (hasAccountLevelPermissions || getters.isAdminForAnyTextbase);
        },
        /**
         * Returns any text-base corresponding with given `textbase_id`.
         *
         * @param {object} state
         * @param {object} getters
         * @return {function(string): Readonly<Textbase>|undefined}
         */
        oneTextbase(state, getters) {
            return (textbase_id) => {
                const out = getters.oneTextbasePermission(textbase_id);
                return (out instanceof TextbasePermission ? out.textbase : undefined);
            }
        },
        /**
         * Returns any text-base level permission corresponding with given `textbase_id`.
         *
         * @param {object} state
         * @return {function(string): Readonly<TextbasePermission>|undefined}
         */
        oneTextbasePermission(state) {
            return (textbase_id) => {
                return state.textbases.find((permission) => {
                    return permission.textbase.id === textbase_id;
                });
            }
        }
    },
    mutations: {
        /**
         * Stores given `permission` as account-level permissions for current user permissions in module state.
         *
         * @param {object} state
         * @param {AccountPermission} permission
         */
        setAccountPermission(state, permission) {
            if (!(permission instanceof AccountPermission)) {
                throw new TypeError(`VueX 'permissions/setAccountPermission' mutation expects payload to be an instance of Account/Permission, ${typeof permission} given`);
            }

            state.account = permission;
        },
        /**
         *
         * @param {object} state
         * @param {Array<Readonly<TextbasePermission>>} permissions
         */
        setAllTextbasePermission(state, permissions){
            state.textbases = permissions;
            state.textbases.sort((permissionA, permissionB) => {
                return permissionA.textbase.name.localeCompare(permissionB.textbase.name);
            });
        },
        /**
         * Stores given `permission` in module state. If a permission corresponding to given `permission` textbase
         * already exists, it's replaced with given `permission`.
         *
         * @param {object} state
         * @param {TextbasePermission} permission
         */
        setTextbasePermission(state, permission) {
            if (!(permission instanceof TextbasePermission)) {
                throw new TypeError(`VueX 'permissions/set' mutation expects payload to be an instance of Permission, ${typeof permission} given`);
            }

            let found = false;

            state.textbases = state.textbases.map((existing_permission) => {
                if (existing_permission.textbase.id === permission.textbase.id) {
                    found = true;
                    return permission;
                } else {
                    return existing_permission;
                }
            });

            if (!found) {
                state.textbases.push(permission);
            }

            state.textbases.sort((permissionA, permissionB) => {
                return permissionA.textbase.name.localeCompare(permissionB.textbase.name);
            });
        }
    },
    namespaced: true,
    state: {
        account: null,
        textbases: []
    }
}
